import { createContext, useState, useContext, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { paymentRepository, tierRepository, addonRepository } from "../../../di/di";
import { useNavigate } from "react-router-dom";
import { PageNotFound } from "../../../../../core/routes/route_constants";
import { StringUtils } from "../../../../../core/utils/string_utils";
import TrackingService from "../../../../data/service/impl/tracking_service_impl";

const trackingService = new TrackingService();


export const PaymentUIAction = Object.freeze({
  initial: "initial",
  navigateToPageNotFound: "navigateToPageNotFound",
  showErrorToast: "showErrorToast",
  showSuccessToast: "showSuccessToast",
});

export const PaymentUIStatus = Object.freeze({
  loading: "loading",
  loaded: "loaded",
  failed: "failed",
});

export const PaymentProcessStatus = Object.freeze({
  initial: "initial",
  processing: "processing",
  success: "success",
  failed: "failed",
});

const PaymentStateContext = createContext();

export const PaymentStateContextProvider = ({ children }) => {
  const [transactionType, setTransactionType] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [message, setMessage] = useState("");
  const [action, setAction] = useState(PaymentUIAction.initial);
  const [uiStatus, setUiStatus] = useState(PaymentUIStatus.loading);
  const [paymentProcessStatus, setPaymentProcessStatus] = useState(PaymentProcessStatus.initial);
  const [showPaymentDetails, setShowPaymentDetails] = useState(true);
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [cardHolderFirstName, setCardHolderFirstName] = useState("");
  const [cardHolderLastName, setCardHolderLastName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvc, setCvc] = useState("");
  const [showCvc, setShowCvc] = useState(false);
  const [cardHolderFirstNameError, setCardHolderFirstNameError] = useState("");
  const [cardHolderLastNameError, setCardHolderLastNameError] = useState("");
  const [cardNumberError, setCardNumberError] = useState("");
  const [expiryDateError, setExpiryDateError] = useState("");
  const [cvcError, setCvcError] = useState("");
  const [billingAddressFirstName, setBillingAddressFirstName] = useState("");
  const [billingAddressLastName, setBillingAddressLastName] = useState("");
  const [billingAddressEmail, setBillingAddressEmail] = useState("");
  const [billingAddressAddressLine1, setBillingAddressAddressLine1] = useState("");
  const [billingAddressCity, setBillingAddressCity] = useState("");
  const [billingAddressState, setBillingAddressState] = useState("");
  const [billingAddressZipCode, setBillingAddressZipCode] = useState("");
  const [billingAddressCountry, setBillingAddressCountry] = useState("");
  const [billingAddressPhone, setBillingAddressPhone] = useState("");
  const [billingAddressFirstNameError, setBillingAddressFirstNameError] = useState("");
  const [billingAddressLastNameError, setBillingAddressLastNameError] = useState("");
  const [billingAddressEmailError, setBillingAddressEmailError] = useState("");
  const [billingAddressAddressLine1Error, setBillingAddressAddressLine1Error] = useState("");
  const [billingAddressCityError, setBillingAddressCityError] = useState("");
  const [billingAddressStateError, setBillingAddressStateError] = useState("");
  const [billingAddressZipCodeError, setBillingAddressZipCodeError] = useState("");
  const [billingAddressCountryError, setBillingAddressCountryError] = useState("");
  const [billingAddressPhoneError, setBillingAddressPhoneError] = useState("");
  const [addon, setAddon] = useState(null);

  const paymentInitialize = (transactionType, transactionId) => {
    if (transactionType === "subscription") {
      paymentSubscriptionInitialize(transactionId);
    } else if (transactionType === "addon") {
      paymentAddonInitialize(transactionId);
    } else {
      setAction(PaymentUIAction.navigateToPageNotFound);
    }
  };

  const paymentSubscriptionInitialize = async (tierId) => {
    setTransactionType("subscription");
    setTransactionId(tierId);
    const result = await tierRepository.getTierById(tierId);
    if (result.success) {
      setUiStatus(PaymentUIStatus.loaded);
    } else {
      setAction(PaymentUIAction.navigateToPageNotFound);
    }
  };

  const paymentAddonInitialize = async (addonId) => {
    setTransactionType("addon");
    setTransactionId(addonId);
    const result = await addonRepository.getAddonById(addonId);
    if (result.success) {
      if (result.data[0].isActive) {
        setAddon(result.data[0]);
        setUiStatus(PaymentUIStatus.loaded);
      } else {
        setAction(PaymentUIAction.navigateToPageNotFound);
      }
    } else {
      setAction(PaymentUIAction.navigateToPageNotFound);
    }
  };

  const handleCardHolderFirstNameChange = (value) => {
    const capitalizedValue = StringUtils.capitalizeWords(value);
    setCardHolderFirstName(capitalizedValue);
    const data = capitalizedValue;
    if (StringUtils.isStringEmpty(data)) {
      setCardHolderFirstNameError("Field is required");
    } else {
      setCardHolderFirstNameError("");
    }
  };

  const handleCardHolderLastNameChange = (value) => {
    const capitalizedValue = StringUtils.capitalizeWords(value);
    setCardHolderLastName(capitalizedValue);
    const data = capitalizedValue;
    if (StringUtils.isStringEmpty(data)) {
      setCardHolderLastNameError("Field is required");
    } else {
      setCardHolderLastNameError("");
    }
  };

  const handleCardNumberChange = (value) => {
    const data = value.replace(/[^0-9]/g, "");
    setCardNumber(data);
    if (StringUtils.isStringEmpty(data)) {
      setCardNumberError("Field is required");
    } else {
      setCardNumberError("");
    }
  };

  const handleExpiryDateChange = (value, nativeEvent) => {
    const input = value;
    let numericValue = input.replace(/[^0-9]/g, "");
    setExpiryDate(numericValue);

    if (StringUtils.isStringEmpty(numericValue)) {
      setExpiryDateError("Field is required");
      return;
    }

    if (numericValue.length > 4) {
      numericValue = numericValue.slice(0, 4);
    }

    let formattedValue = numericValue;
    if (numericValue.length >= 2) {
      formattedValue = numericValue.slice(0, 2) + "/" + numericValue.slice(2);
      if (numericValue.length === 1 && input.endsWith("/")) {
        formattedValue = numericValue.slice(0, 1);
      }
      if (numericValue.length === 2 && input.length === 2) {
        formattedValue = numericValue;
      }
      if (numericValue.length === 0) {
        formattedValue = "";
      }
    }

    if (input.endsWith("/") && numericValue.length === 2 && input.length === 3) {
      formattedValue = numericValue.slice(0, 2) + "/";
    } else if (input.length === 2 && expiryDate.length === 3 && nativeEvent != undefined && nativeEvent.inputType === "deleteContentBackward") {
      formattedValue = input.slice(0, 2);
    }

    setExpiryDate(formattedValue);

    // Check if the format is valid (MM/YY)
    const formatRegex = /^\d{2}\/\d{2}$/;
    if (formattedValue.length > 0 && !formatRegex.test(formattedValue)) {
      setExpiryDateError("Format invalid (MM/YY)");
      return;
    }

    if (formattedValue.length === 5) {
      const [monthStr, yearStr] = formattedValue.split("/");
      const month = parseInt(monthStr, 10);
      const year = parseInt(`20${yearStr}`, 10);
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (month < 1 || month > 12) {
        setExpiryDateError("Invalid month.");
      } else if (year < currentYear || (year === currentYear && month < currentMonth)) {
        setExpiryDateError("Card is expired.");
      } else {
        setExpiryDateError("");
      }
    } else if (formattedValue.length < 5 && expiryDateError) {
      setExpiryDateError("");
    }
  };

  const handleCvcChange = (value) => {
    const data = value.replace(/[^0-9]/g, "");
    setCvc(data);
    if (StringUtils.isStringEmpty(data)) {
      setCvcError("Field is required");
    } else {
      setCvcError("");
    }
  };

  const handleBillingAddressFirstNameChange = (value) => {
    const capitalizedValue = StringUtils.capitalizeWords(value);
    setBillingAddressFirstName(capitalizedValue);
    const data = capitalizedValue;
    if (StringUtils.isStringEmpty(data)) {
      setBillingAddressFirstNameError("Field is required");
    } else {
      setBillingAddressFirstNameError("");
    }
  };

  const handleBillingAddressLastNameChange = (value) => {
    const capitalizedValue = StringUtils.capitalizeWords(value);
    setBillingAddressLastName(capitalizedValue);
    const data = capitalizedValue;
    if (StringUtils.isStringEmpty(data)) {
      setBillingAddressLastNameError("Field is required");
    } else {
      setBillingAddressLastNameError("");
    }
  };

  const handleBillingAddressEmailChange = (value) => {
    setBillingAddressEmail(value);
    const emailValidationResult = StringUtils.isEmailValid(value);
    if (!emailValidationResult.isValid) {
      setBillingAddressEmailError(emailValidationResult.message);
    } else {
      setBillingAddressEmailError("");
    }
  };

  const handleBillingAddressAddressLine1Change = (value) => {
    setBillingAddressAddressLine1(value);
    if (StringUtils.isStringEmpty(value)) {
      setBillingAddressAddressLine1Error("Field is required");
    } else {
      setBillingAddressAddressLine1Error("");
    }
  };

  const handleBillingAddressCityChange = (value) => {
    setBillingAddressCity(value);
    if (StringUtils.isStringEmpty(value)) {
      setBillingAddressCityError("Field is required");
    } else {
      setBillingAddressCityError("");
    }
  };

  const handleBillingAddressStateChange = (value) => {
    setBillingAddressState(value);
    if (StringUtils.isStringEmpty(value)) {
      setBillingAddressStateError("Field is required");
    } else {
      setBillingAddressStateError("");
    }
  };

  const handleBillingAddressZipCodeChange = (value) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setBillingAddressZipCode(numericValue);
    if (StringUtils.isStringEmpty(numericValue)) {
      setBillingAddressZipCodeError("Field is required");
    } else {
      setBillingAddressZipCodeError("");
    }
  };

  const handleBillingAddressCountryChange = (value) => {
    setBillingAddressCountry(value);
    if (StringUtils.isStringEmpty(value)) {
      setBillingAddressCountryError("Field is required");
    } else {
      setBillingAddressCountryError("");
    }
  };

  const handleBillingAddressPhoneChange = (value) => {
    setBillingAddressPhone(value);
    if (StringUtils.isStringEmpty(value)) {
      setBillingAddressPhoneError("Field is required");
    } else {
      setBillingAddressPhoneError("");
    }
  };

  const validatePaymentDetails = () => {
    handleCardHolderFirstNameChange(cardHolderFirstName);
    handleCardHolderLastNameChange(cardHolderLastName);
    handleCardNumberChange(cardNumber);
    handleExpiryDateChange(expiryDate);
    handleCvcChange(cvc);
    if (
      StringUtils.isStringEmpty(cardHolderFirstName) ||
      StringUtils.isStringEmpty(cardHolderLastName) ||
      StringUtils.isStringEmpty(cardNumber) ||
      StringUtils.isStringEmpty(expiryDate) ||
      StringUtils.isStringEmpty(cvc) ||
      cardHolderFirstNameError ||
      cardHolderLastNameError ||
      cardNumberError ||
      expiryDateError ||
      cvcError
    ) {
      return false;
    }
    return true;
  };

  const validateBillingAddress = () => {
    handleBillingAddressFirstNameChange(billingAddressFirstName);
    handleBillingAddressLastNameChange(billingAddressLastName);
    handleBillingAddressEmailChange(billingAddressEmail);
    handleBillingAddressAddressLine1Change(billingAddressAddressLine1);
    handleBillingAddressCityChange(billingAddressCity);
    handleBillingAddressStateChange(billingAddressState);
    handleBillingAddressZipCodeChange(billingAddressZipCode);
    handleBillingAddressCountryChange(billingAddressCountry);
    handleBillingAddressPhoneChange(billingAddressPhone);
    if (
      StringUtils.isStringEmpty(billingAddressFirstName) ||
      StringUtils.isStringEmpty(billingAddressLastName) ||
      StringUtils.isStringEmpty(billingAddressEmail) ||
      StringUtils.isStringEmpty(billingAddressAddressLine1) ||
      StringUtils.isStringEmpty(billingAddressCity) ||
      StringUtils.isStringEmpty(billingAddressState) ||
      StringUtils.isStringEmpty(billingAddressZipCode) ||
      StringUtils.isStringEmpty(billingAddressCountry) ||
      StringUtils.isStringEmpty(billingAddressPhone) ||
      billingAddressFirstNameError ||
      billingAddressLastNameError ||
      billingAddressEmailError ||
      billingAddressAddressLine1Error ||
      billingAddressCityError ||
      billingAddressStateError ||
      billingAddressZipCodeError ||
      billingAddressCountryError ||
      billingAddressPhoneError
    ) {
      return false;
    }
    return true;
  };

  const handleBillingAddressVisibilityChange = () => {
    if (!showBillingAddress) {
      if (!validatePaymentDetails()) {
        setMessage("Please fix the payment details before proceeding.");
        setAction(PaymentUIAction.showErrorToast);
      } else {
        setShowBillingAddress(true);
        setShowPaymentDetails(false);
      }
    } else {
      if (!validateBillingAddress()) {
        setMessage("Please fix the billing address before proceeding.");
        setAction(PaymentUIAction.showErrorToast);
      } else {
        setShowBillingAddress(false);
        setShowPaymentDetails(true);
      }
    }
  };

  const handlePaymentSubmit = () => {
    /// Validate the payment details
    if (!validatePaymentDetails()) {
      setMessage("Please fix the payment details before proceeding.");
      setAction(PaymentUIAction.showErrorToast);
      setShowBillingAddress(false);
      setShowPaymentDetails(true);
      return;
    }

    /// Validate the billing address
    if (!validateBillingAddress()) {
      setMessage("Please fix the billing address before proceeding.");
      setAction(PaymentUIAction.showErrorToast);
      setShowBillingAddress(true);
      setShowPaymentDetails(false);
      return;
    }

    /// Perform API request
    if (transactionType === "subscription") {
      const tierId = transactionId;
      setPaymentProcessStatus(PaymentProcessStatus.processing);
      paymentRepository.processSubscriptionPayment(
        tierId,
        {
          cardHolderFirstName: cardHolderFirstName,
          cardHolderLastName: cardHolderLastName,
          cardNumber: cardNumber,
          expiryDate: expiryDate,
          cvc: cvc,
        },
        {
          billingAddressFirstName: billingAddressFirstName,
          billingAddressLastName: billingAddressLastName,
          billingAddressEmail: billingAddressEmail,
          billingAddressPhone: billingAddressPhone,
          billingAddressAddressLine1: billingAddressAddressLine1,
          billingAddressCity: billingAddressCity,
          billingAddressState: billingAddressState,
          billingAddressZipCode: billingAddressZipCode,
          billingAddressCountry: billingAddressCountry,
        },
        {
          onSuccess: () => {
            setPaymentProcessStatus(PaymentProcessStatus.success);
            setMessage("Payment successful");
            setAction(PaymentUIAction.showSuccessToast);
          },
          onError: (error) => {
            setPaymentProcessStatus(PaymentProcessStatus.failed);
            setMessage(error);
            setAction(PaymentUIAction.showErrorToast);
          },
          onDone: () => {},
          onNoBillingAddress: () => {
            setPaymentProcessStatus(PaymentProcessStatus.failed);
            setMessage("Please enter a billing address before proceeding.");
            setAction(PaymentUIAction.showErrorToast);
          },
          onNoPaymentMethod: () => {
            setPaymentProcessStatus(PaymentProcessStatus.failed);
            setMessage("Please enter a payment method before proceeding.");
            setAction(PaymentUIAction.showErrorToast);
          },
        }
      );
    } else if (transactionType === "addon") {
      const addonId = transactionId;
      setPaymentProcessStatus(PaymentProcessStatus.processing);
      paymentRepository.processAddonPayment(
        addonId,
        {
          cardHolderFirstName: cardHolderFirstName,
          cardHolderLastName: cardHolderLastName,
          cardNumber: cardNumber,
          expiryDate: expiryDate,
          cvc: cvc,
        },
        {
          billingAddressFirstName: billingAddressFirstName,
          billingAddressLastName: billingAddressLastName,
          billingAddressEmail: billingAddressEmail,
          billingAddressPhone: billingAddressPhone,
          billingAddressAddressLine1: billingAddressAddressLine1,
          billingAddressCity: billingAddressCity,
          billingAddressState: billingAddressState,
          billingAddressZipCode: billingAddressZipCode,
          billingAddressCountry: billingAddressCountry,
        },
        {
          onSuccess: () => {
            setPaymentProcessStatus(PaymentProcessStatus.success);
            setMessage("Payment successful");
            setAction(PaymentUIAction.showSuccessToast);
          },
          onError: (error) => {
            setPaymentProcessStatus(PaymentProcessStatus.failed);
            setMessage(error);
            setAction(PaymentUIAction.showErrorToast);
          },
          onDone: () => {},
          onNoBillingAddress: () => {
            setPaymentProcessStatus(PaymentProcessStatus.failed);
            setMessage("Please enter a billing address before proceeding.");
            setAction(PaymentUIAction.showErrorToast);
          },
          onNoPaymentMethod: () => {
            setPaymentProcessStatus(PaymentProcessStatus.failed);
            setMessage("Please enter a payment method before proceeding.");
            setAction(PaymentUIAction.showErrorToast);
          },
        }
      );
    }
  };

  const values = {
    /// States
    message,
    action,
    uiStatus,
    paymentProcessStatus,
    showPaymentDetails,
    showBillingAddress,
    cardHolderFirstName,
    cardHolderLastName,
    cardNumber,
    expiryDate,
    cvc,
    showCvc,
    cardHolderFirstNameError,
    cardHolderLastNameError,
    cardNumberError,
    expiryDateError,
    cvcError,
    billingAddressFirstName,
    billingAddressLastName,
    billingAddressEmail,
    billingAddressAddressLine1,
    billingAddressCity,
    billingAddressState,
    billingAddressZipCode,
    billingAddressCountry,
    billingAddressFirstNameError,
    billingAddressLastNameError,
    billingAddressEmailError,
    billingAddressAddressLine1Error,
    billingAddressCityError,
    billingAddressStateError,
    billingAddressZipCodeError,
    billingAddressCountryError,
    billingAddressPhoneError,
    addon,

    /// State Changing Functions
    setShowCvc,
    setAction,

    /// Handlers
    paymentInitialize,
    handleCardHolderFirstNameChange,
    handleCardHolderLastNameChange,
    handleCardNumberChange,
    handleExpiryDateChange,
    handleCvcChange,
    handleBillingAddressVisibilityChange,
    handlePaymentSubmit,
    handleBillingAddressFirstNameChange,
    handleBillingAddressLastNameChange,
    handleBillingAddressEmailChange,
    handleBillingAddressAddressLine1Change,
    handleBillingAddressCityChange,
    handleBillingAddressStateChange,
    handleBillingAddressZipCodeChange,
    handleBillingAddressCountryChange,
    handleBillingAddressPhoneChange,
  };

  return <PaymentStateContext.Provider value={values}>{children}</PaymentStateContext.Provider>;
};

export const usePaymentStateContext = () => useContext(PaymentStateContext);
